import { useFetcher } from 'react-router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button as RoccoButton } from '@rocco/components/button';
import { Input as RoccoInput } from '@rocco/components/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { REQUEST_EMAIL_LOGIN_CODE_API_ROUTE } from '~/routes/remix-api.user.request-email-login-code';
import { VERIFY_EMAIL_LOGIN_CODE_API_ROUTE } from '~/routes/remix-api.user.verify-email-login-code';

const SIX_DIGIT_CODE_MESSAGE = 'Enter the 6-digit code from your email';

/**
 * Login modal primary submit — matches aria `Button` (`color="dark"`, `size={18}`, `fluid`):
 * charcoal fill, square corners, 18px label (not Rocco pill `color="dark"`).
 */
const loginModalSubmitButtonClass =
    'w-full! rounded-none! border-0! bg-[#343434]! px-4 py-4 text-lg! font-normal! leading-none text-white! hover:bg-[#4a4a4a]! hover:text-white!';

const SUBMIT_LABEL_FADE_MS = 300;
const RESEND_ACK_DISPLAY_MS = 1000;

/** Dedupes Strict Mode remount while a send is in flight (fetcher persists across remounts). */
const autoSendInFlightByEmail = new Set<string>();

interface LoginModalSubmitButtonProps {
    type: 'submit' | 'button';
    children: ReactNode;
    isLoading?: boolean;
    disabled?: boolean;
    labelVisible?: boolean;
    onClick?: () => void;
}

/** White circle + compact dark tick — visible on the charcoal submit button. */
function CodeSentCheckIcon() {
    return (
        <span
            aria-hidden
            className="inline-flex size-[1em] shrink-0 items-center justify-center rounded-full bg-white text-[#343434]"
        >
            <svg
                viewBox="0 0 16 16"
                className="size-[0.55em] shrink-0"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M4.25 8.25L7 11L11.75 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

function LoginModalSubmitButton({
    type,
    children,
    isLoading,
    disabled = false,
    labelVisible = true,
    onClick,
}: LoginModalSubmitButtonProps) {
    return (
        <RoccoButton
            type={type}
            color="grayDark"
            shape="square"
            weight="normal"
            textSize="large"
            className={loginModalSubmitButtonClass}
            isLoading={isLoading}
            disabled={disabled || isLoading}
            onClick={onClick}
        >
            <span
                className={`inline-flex items-center justify-center gap-2 transition-opacity duration-300 ${
                    labelVisible ? 'opacity-100' : 'opacity-0'
                }`}
            >
                {children}
            </span>
        </RoccoButton>
    );
}

/** Matches server normalisation: strip spaces/dashes, then require exactly six digits. */
const isSixDigitLoginCode = (value: string): boolean =>
    /^\d{6}$/.test(value.replace(/[\s-]/g, ''));

const codeSchema = z.object({
    code: z
        .string()
        .trim()
        .refine(isSixDigitLoginCode, {
            message: SIX_DIGIT_CODE_MESSAGE,
        }),
});

type CodeForm = z.infer<typeof codeSchema>;

type EmailCodeFetcherData = { ok?: boolean; message?: string };

interface EmailCodeEntryStepProps {
    email: string;
    onBack: () => void;
    onSuccess: () => void;
    /** When true, request a code as soon as the step mounts. */
    autoSend?: boolean;
    /** Primary submit label — matches login form (e.g. "Login"). */
    submitLabel?: string;
    /** When true, back is rendered in the modal header instead of inline. */
    hideBack?: boolean;
    /** Modal header chrome (back visibility) while on this step. */
    onChromeStateChange?: (state: EmailCodeStepChromeState) => void;
}

export interface EmailCodeStepChromeState {
    sendingCode: boolean;
    requestSent: boolean;
    requestError: string | null;
    autoSend: boolean;
}

export function EmailCodeEntryStep({
    email,
    onBack,
    onSuccess,
    autoSend = false,
    submitLabel = 'Login',
    hideBack = false,
    onChromeStateChange,
}: EmailCodeEntryStepProps) {
    const normalizedEmail = email.trim().toLowerCase();
    const verifyFetcher = useFetcher<EmailCodeFetcherData>();
    const requestFetcher = useFetcher<EmailCodeFetcherData>();
    const [requestSent, setRequestSent] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [submitLabelPhase, setSubmitLabelPhase] = useState<'login' | 'code-sent'>(
        'login',
    );
    const [submitLabelVisible, setSubmitLabelVisible] = useState(true);
    const [resendSending, setResendSending] = useState(false);
    const handledRequestFetcherKeyRef = useRef<string | null>(null);
    const handledVerifyFetcherKeyRef = useRef<string | null>(null);
    /** Ignore stale fetcher.data until sendCode() runs in this mount/session. */
    const awaitingRequestResponseRef = useRef(false);
    /** Ensures autoSend only triggers one request per step visit (not on fetcher idle). */
    const autoSendInitiatedRef = useRef(false);
    const resendAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const sendingCode = requestFetcher.state !== 'idle';

    useLayoutEffect(() => {
        if (requestFetcher.state !== 'idle') {
            awaitingRequestResponseRef.current = true;
        }
    }, [requestFetcher.state]);

    useEffect(() => {
        onChromeStateChange?.({
            sendingCode,
            requestSent,
            requestError,
            autoSend,
        });
    }, [
        autoSend,
        onChromeStateChange,
        requestError,
        requestSent,
        sendingCode,
    ]);

    const form = useForm<CodeForm>({
        resolver: zodResolver(codeSchema),
        defaultValues: { code: '' },
    });

    const sendCode = useCallback(() => {
        if (!normalizedEmail) {
            setRequestError('Enter your email address first.');
            return;
        }

        if (
            requestFetcher.state !== 'idle' ||
            awaitingRequestResponseRef.current
        ) {
            return;
        }

        setRequestError(null);
        form.clearErrors('code');
        handledRequestFetcherKeyRef.current = null;
        awaitingRequestResponseRef.current = true;
        autoSendInFlightByEmail.add(normalizedEmail);
        requestFetcher.submit(
            { email: normalizedEmail },
            { method: 'post', action: REQUEST_EMAIL_LOGIN_CODE_API_ROUTE },
        );
    }, [form, normalizedEmail, requestFetcher]);

    const clearResendAckTimeout = useCallback(() => {
        if (resendAckTimeoutRef.current !== null) {
            clearTimeout(resendAckTimeoutRef.current);
            resendAckTimeoutRef.current = null;
        }
    }, []);

    const handleResendCode = useCallback(() => {
        clearResendAckTimeout();
        setSubmitLabelPhase('login');
        setSubmitLabelVisible(true);
        form.resetField('code', { defaultValue: '' });
        if (verifyFetcher.data) {
            handledVerifyFetcherKeyRef.current = `${verifyFetcher.state}:${JSON.stringify(verifyFetcher.data)}`;
        }
        if (!normalizedEmail) {
            setRequestError('Enter your email address first.');
            return;
        }
        setResendSending(true);
        sendCode();
    }, [
        clearResendAckTimeout,
        form,
        normalizedEmail,
        sendCode,
        verifyFetcher.data,
        verifyFetcher.state,
    ]);

    const showResendAckOnSubmitButton = useCallback(() => {
        clearResendAckTimeout();
        setSubmitLabelPhase('code-sent');
        setSubmitLabelVisible(true);

        resendAckTimeoutRef.current = setTimeout(() => {
            setSubmitLabelVisible(false);

            resendAckTimeoutRef.current = setTimeout(() => {
                setSubmitLabelPhase('login');
                setSubmitLabelVisible(true);
                resendAckTimeoutRef.current = null;
            }, SUBMIT_LABEL_FADE_MS);
        }, RESEND_ACK_DISPLAY_MS);
    }, [clearResendAckTimeout]);

    useEffect(
        () => () => {
            clearResendAckTimeout();
        },
        [clearResendAckTimeout],
    );

    useEffect(() => {
        if (!autoSend || !normalizedEmail || requestSent) {
            return;
        }

        if (autoSendInitiatedRef.current) {
            return;
        }

        if (autoSendInFlightByEmail.has(normalizedEmail)) {
            autoSendInitiatedRef.current = true;
            awaitingRequestResponseRef.current = true;
            return;
        }

        autoSendInitiatedRef.current = true;
        sendCode();
        // Intentionally omit requestFetcher.state — re-running when the fetcher
        // returns to idle caused a second send before requestSent was set.
    }, [autoSend, normalizedEmail, requestSent, sendCode]);

    useEffect(() => {
        if (
            requestFetcher.state !== 'idle' ||
            !requestFetcher.data ||
            !awaitingRequestResponseRef.current
        ) {
            return;
        }

        const responseKey = `${requestFetcher.state}:${JSON.stringify(requestFetcher.data)}`;
        if (handledRequestFetcherKeyRef.current === responseKey) {
            return;
        }
        handledRequestFetcherKeyRef.current = responseKey;

        if (requestFetcher.data.ok) {
            awaitingRequestResponseRef.current = false;
            autoSendInFlightByEmail.delete(normalizedEmail);
            setResendSending(false);
            setRequestError(null);
            form.clearErrors('code');
            setRequestSent(true);
            showResendAckOnSubmitButton();
            return;
        }

        awaitingRequestResponseRef.current = false;
        autoSendInFlightByEmail.delete(normalizedEmail);
        setResendSending(false);
        clearResendAckTimeout();
        setSubmitLabelPhase('login');
        setSubmitLabelVisible(true);
        const errorMessage =
            requestFetcher.data.message ??
            'Could not send the code. Try again in a moment.';
        setRequestError(errorMessage);
        if (requestSent) {
            form.setError('code', { message: errorMessage });
        }
    }, [
        clearResendAckTimeout,
        form,
        requestFetcher.data,
        requestFetcher.state,
        requestSent,
        normalizedEmail,
        showResendAckOnSubmitButton,
    ]);

    useEffect(() => {
        if (verifyFetcher.state !== 'idle' || !verifyFetcher.data) {
            return;
        }

        const responseKey = `${verifyFetcher.state}:${JSON.stringify(verifyFetcher.data)}`;
        if (handledVerifyFetcherKeyRef.current === responseKey) {
            return;
        }
        handledVerifyFetcherKeyRef.current = responseKey;

        if (verifyFetcher.data.ok) {
            onSuccess();
            return;
        }

        form.setError('code', {
            message:
                verifyFetcher.data.message ??
                'This code is invalid or has expired.',
        });
    }, [verifyFetcher.data, verifyFetcher.state, form, onSuccess]);

    const handleVerify = form.handleSubmit((data) => {
        handledVerifyFetcherKeyRef.current = null;
        form.clearErrors('code');
        verifyFetcher.submit(
            { email: normalizedEmail, code: data.code },
            { method: 'post', action: VERIFY_EMAIL_LOGIN_CODE_API_ROUTE },
        );
    });

    return (
        <Form {...form}>
            <div className="mt-4 flex flex-col gap-4">
                <p className="text-center text-sm text-neutral-600">
                    {requestSent
                        ? `We've sent a code to ${normalizedEmail}. Check your inbox and enter it below, or tap the link in the email.`
                        : autoSend
                          ? `Sending a one-time sign-in code to ${normalizedEmail}…`
                          : `Send a one-time sign-in code to ${normalizedEmail}.`}
                </p>

                {!requestSent ? (
                    autoSend && !requestError ? (
                        <LoginModalSubmitButton
                            type="button"
                            isLoading={sendingCode}
                            disabled
                        >
                            Sending code…
                        </LoginModalSubmitButton>
                    ) : (
                        <LoginModalSubmitButton
                            type="button"
                            isLoading={sendingCode}
                            onClick={sendCode}
                        >
                            Email me a code
                        </LoginModalSubmitButton>
                    )
                ) : (
                    <form
                        onSubmit={handleVerify}
                        className="flex flex-col gap-4"
                    >
                        <FormField
                            control={form.control}
                            name="code"
                            render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                    <FormLabel>Sign-in code</FormLabel>
                                    <FormControl>
                                        <RoccoInput
                                            {...field}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            placeholder="123-456"
                                        />
                                    </FormControl>
                                    <FormMessage>{error?.message}</FormMessage>
                                </FormItem>
                            )}
                        />
                        <LoginModalSubmitButton
                            type="submit"
                            isLoading={
                                verifyFetcher.state !== 'idle' || resendSending
                            }
                            labelVisible={submitLabelVisible}
                        >
                            {submitLabelPhase === 'code-sent' ? (
                                <>
                                    Code sent
                                    <CodeSentCheckIcon />
                                </>
                            ) : (
                                submitLabel
                            )}
                        </LoginModalSubmitButton>
                        <button
                            type="button"
                            className="text-sm text-neutral-600 underline"
                            onClick={handleResendCode}
                            disabled={sendingCode || resendSending}
                        >
                            Resend code
                        </button>
                    </form>
                )}

                {requestError && !requestSent ? (
                    <p className="text-sm text-red-600">{requestError}</p>
                ) : null}

                {!hideBack ? (
                    <button
                        type="button"
                        className="text-sm text-neutral-600 underline"
                        onClick={onBack}
                    >
                        Back
                    </button>
                ) : null}
            </div>
        </Form>
    );
}
